/**
 * User management service
 */

import { apiClient } from './api';
import {
  User,
  UserCreate,
  UserUpdate,
  UserProfileUpdate,
  UserWithSSHKeys,
  SSHKey,
  SSHKeyCreate,
  ChangePasswordRequest,
  QueryParams,
} from '../types';

export class UserService {
  async getUsers(params?: QueryParams): Promise<User[]> {
    return apiClient.get<User[]>('/users/', params);
  }

  async getLearners(): Promise<User[]> {
    return apiClient.get<User[]>('/instructor/learners');
  }

  async getLearnersForInstructor(params?: {
    assigned_instructor_id?: number;
    unassigned?: boolean;
    cohort?: string;
    search?: string;
  }): Promise<User[]> {
    return apiClient.get<User[]>('/instructor/learners', params);
  }

  async getInstructors(): Promise<User[]> {
    return apiClient.get<User[]>('/instructor/instructors');
  }

  async createUser(userData: UserCreate): Promise<User> {
    return apiClient.post<User>('/users/', userData);
  }

  async getUser(userId: number): Promise<User> {
    return apiClient.get<User>(`/users/${userId}`);
  }

  async updateUser(userId: number, userData: UserUpdate): Promise<User> {
    return apiClient.put<User>(`/users/${userId}`, userData);
  }

  async deleteUser(userId: number): Promise<{ message: string }> {
    return apiClient.delete(`/users/${userId}`);
  }

  async getCurrentUserWithKeys(): Promise<UserWithSSHKeys> {
    return apiClient.get<UserWithSSHKeys>('/users/me/ssh-keys');
  }

  async changePassword(data: ChangePasswordRequest): Promise<{ message: string }> {
    return apiClient.post<{ message: string }>('/users/me/change-password', data);
  }

  async updateProfile(data: UserProfileUpdate): Promise<User> {
    return apiClient.patch<User>('/users/me/profile', data);
  }

  async getSSHKeys(): Promise<SSHKey[]> {
    return apiClient.get<SSHKey[]>('/users/me/ssh-keys');
  }

  async createSSHKey(keyData: SSHKeyCreate): Promise<SSHKey> {
    return apiClient.post<SSHKey>('/users/me/ssh-keys', keyData);
  }

  async deleteSSHKey(keyId: number): Promise<{ message: string }> {
    return apiClient.delete(`/users/me/ssh-keys/${keyId}`);
  }

  // Spaces credentials
  async getSpacesCredentials(): Promise<any[]> {
    return apiClient.get<any[]>('/users/me/spaces-credentials');
  }

  async createSpacesCredential(data: { name: string; access_key_id: string; secret_key: string; region?: string; endpoint?: string; }): Promise<any> {
    return apiClient.post<any>('/users/me/spaces-credentials', data);
  }

  async deleteSpacesCredential(id: number): Promise<{ message: string }> {
    return apiClient.delete(`/users/me/spaces-credentials/${id}`);
  }
}

export const userService = new UserService();
